import React, { useMemo, useState, useEffect } from 'react';
import CoordPickerModal from './CoordPickerModal';
import SpeciesAutocomplete from './SpeciesAutocomplete';
import SelectedSpeciesSummary from './SelectedSpeciesSummary';
import MoreDetailsSection from './MoreDetailsSection';
import { resolveHarvestCategory } from './harvestFlow';

const SEASONS = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
];

const FALLBACK_TAXONOMY = {
  categories: [
    { key: 'plants_medicine', label: 'Plants & Medicine', subcategoryLabel: 'Method / Context', subtypes: ['Foraging', 'Berry Picking', 'Other'] },
    { key: 'hunting', label: 'Hunting', subcategoryLabel: 'Method / Context', subtypes: ['Rifle', 'Bow', 'Other'] },
    { key: 'birds', label: 'Birds', subcategoryLabel: 'Method / Context', subtypes: ['Decoy Hunt', 'Blind', 'Other'] },
    { key: 'fishing', label: 'Fishing', subcategoryLabel: 'Method / Context', subtypes: ['From Shore', 'From Boat', 'Other'] },
    { key: 'environmental', label: 'Environmental', subcategoryLabel: 'Report Type', subtypes: ['Pollution', 'Habitat damage', 'Other'] },
    { key: 'other', label: 'Other', subcategoryLabel: 'Sub-type', subtypes: [] },
  ],
  units: ['kg', 'lbs', 'count', 'buckets', 'animal'],
  abundanceOptions: [
    { value: 'sparse', label: 'Sparse' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'abundant', label: 'Abundant' },
    { value: 'very_abundant', label: 'Very Abundant' },
  ],
  popularSpecies: ['Walleye (Pickerel)', 'Moose', 'White-tailed Deer', 'Chaga', 'Blueberry'],
  questionVisibility: {},
};

const inputStyle = {
  display: 'block', width: '100%', padding: '8px 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14, color: '#374151',
};

const fieldStyle = { marginBottom: 16 };

function Field({ label, required, children }) {
  const fieldId = React.useId();
  let labelledControlAssigned = false;
  const labelledChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || labelledControlAssigned) return child;
    if (!['input', 'select', 'textarea'].includes(child.type)) return child;
    labelledControlAssigned = true;
    return React.cloneElement(child, { id: child.props.id || fieldId });
  });

  return (
    <div style={fieldStyle}>
      <label style={labelStyle} htmlFor={fieldId}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
      </label>
      {labelledChildren}
    </div>
  );
}

function seasonFromDate(dateStr) {
  if (!dateStr) return '';
  const month = parseInt(dateStr.slice(5, 7), 10);
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter';
}

function emptyForm() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    category: '',
    subcategory: '',
    species: '',
    latitude: '',
    longitude: '',
    locationDescription: '',
    harvestDate: today,
    season: seasonFromDate(today),
    quantity: '',
    unit: '',
    unitOther: '',
    abundance: '',
    observations: '',
    traditionalKnowledge: '',
    isTraditionalUse: false,
    speciesId: '',
    harvestDetails: {},
  };
}

export default function HarvestForm({
  tenantId,
  onSubmit,
  onCancel,
  loading,
  error,
  initial,
  basicMode = false,
  allowedBounds = null,
  allowedBoundsLabel = '',
  mapPickedCoords = null,
  taxonomy = null,
  onSpeciesSelect = null,
  editMode = false,
}) {
  const [form, setForm] = useState({ ...emptyForm(), ...initial });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(editMode);
  const [formError, setFormError] = useState('');

  const activeTaxonomy = taxonomy || FALLBACK_TAXONOMY;
  const categories = activeTaxonomy.categories?.length ? activeTaxonomy.categories : FALLBACK_TAXONOMY.categories;
  const units = activeTaxonomy.units?.length ? activeTaxonomy.units : FALLBACK_TAXONOMY.units;
  const abundanceOptions = activeTaxonomy.abundanceOptions?.length ? activeTaxonomy.abundanceOptions : FALLBACK_TAXONOMY.abundanceOptions;
  const popularSpecies = activeTaxonomy.popularSpecies?.length ? activeTaxonomy.popularSpecies : FALLBACK_TAXONOMY.popularSpecies;
  const questionVisibility = activeTaxonomy.questionVisibility || {};
  const hasMapPickedLocation = basicMode && mapPickedCoords
    && !Number.isNaN(parseFloat(mapPickedCoords.lat))
    && !Number.isNaN(parseFloat(mapPickedCoords.lng));

  const selectedCategory = useMemo(
    () => categories.find((c) => c.key === form.category) || null,
    [categories, form.category]
  );

  const subs = selectedCategory?.subtypes || [];
  const subcatLabel = selectedCategory?.subcategoryLabel || 'Sub-type';
  const isHuntingGame = ['hunting', 'birds'].includes(form.category);
  const isFishing = form.category === 'fishing';
  const isGathering = form.category === 'plants_medicine';
  const usesFocusedHarvestQuestions = isHuntingGame || isFishing || isGathering;

  const filteredPopular = useMemo(() => {
    if (!form.category) return popularSpecies.slice(0, 8);
    return popularSpecies.slice(0, 8);
  }, [form.category, popularSpecies]);

  const speciesCategoryMapLower = useMemo(() => {
    const speciesCategoryMap = activeTaxonomy.speciesCategoryMap
      && typeof activeTaxonomy.speciesCategoryMap === 'object'
      ? activeTaxonomy.speciesCategoryMap
      : {};
    const map = {};
    Object.entries(speciesCategoryMap).forEach(([species, category]) => {
      const key = String(species || '').trim().toLowerCase();
      const categoryKey = String(category || '').trim();
      if (!key || !categoryKey) return;
      map[key] = categoryKey;
    });
    return map;
  }, [activeTaxonomy.speciesCategoryMap]);

  const isFieldEnabled = (fieldKey) => {
    if (editMode) return true;
    if (!form.category) return true;
    const allowed = questionVisibility[fieldKey];
    if (!Array.isArray(allowed)) return true;
    return allowed.includes(form.category);
  };

  useEffect(() => {
    if (!pickerOpen) return;
    document.body.classList.add('ccp-picker-open');
    return () => document.body.classList.remove('ccp-picker-open');
  }, [pickerOpen]);

  useEffect(() => {
    const next = { ...emptyForm(), ...(initial || {}) };
    if (next.unit && !units.includes(next.unit)) {
      next.unitOther = next.unit;
      next.unit = '__other__';
    }
    setForm(next);
    setFormError('');
  }, [JSON.stringify(initial), units]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapPickedCoords) return;
    const lat = parseFloat(mapPickedCoords.lat);
    const lng = parseFloat(mapPickedCoords.lng);
    if (isNaN(lat) || isNaN(lng)) return;
    setForm((prev) => ({
      ...prev,
      latitude: String(parseFloat(lat.toFixed(6))),
      longitude: String(parseFloat(lng.toFixed(6))),
    }));
    setGpsError('');
  }, [mapPickedCoords]);

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }));
        setGpsLoading(false);
      },
      () => {
        setGpsError('Could not get your location. Try picking on the map instead.');
        setGpsLoading(false);
      },
      { timeout: 10000 }
    );
  }

  function set(field) {
    return (e) => {
      const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: val };
        if (field === 'category') {
          next.subcategory = '';
        }
        if (field === 'species') {
          const speciesKey = String(val || '').trim().toLowerCase();
          const mappedCategory = speciesCategoryMapLower[speciesKey];
          if (mappedCategory && mappedCategory !== next.category) {
            next.category = mappedCategory;
            next.subcategory = '';
          }
        }
        if (field === 'harvestDate') next.season = seasonFromDate(val);
        if (field === 'unit' && val !== '__other__') next.unitOther = '';
        return next;
      });
      setFormError('');
    };
  }

  function handlePickerConfirm(lat, lng) {
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
  }

  function setHarvestDetail(field) {
    return (e) => {
      const value = e.target.value;
      setForm((prev) => ({
        ...prev,
        harvestDetails: { ...(prev.harvestDetails || {}), [field]: value },
      }));
      setFormError('');
    };
  }

  function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    if (!form.category) {
      setFormError('Please choose a category.');
      return;
    }

    if (form.unit === '__other__' && !String(form.unitOther || '').trim()) {
      setFormError('Please provide a custom unit value.');
      return;
    }

    const latVal = parseFloat(form.latitude);
    const lngVal = parseFloat(form.longitude);
    if (editMode) {
      if (!form.harvestDate || !form.season) {
        setFormError('Choose a harvest date and season.');
        return;
      }
      if ((form.latitude !== '' || form.longitude !== '') && (
        form.latitude === '' || form.longitude === '' || !Number.isFinite(Number(form.latitude))
        || !Number.isFinite(Number(form.longitude)) || Math.abs(Number(form.latitude)) > 90 || Math.abs(Number(form.longitude)) > 180
      )) {
        setFormError('Provide both valid coordinates, or clear both to remove the location.');
        return;
      }
      for (const [label, value] of [['Quantity', form.quantity], ['Caught quantity', form.harvestDetails?.caughtQuantity],
        ['Size', form.harvestDetails?.sizeValue]]) {
        if (value != null && value !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
          setFormError(`${label} must be zero or greater.`);
          return;
        }
      }
    }
    if (allowedBounds && !isNaN(latVal) && !isNaN(lngVal)) {
      const { minLat, maxLat, minLng, maxLng } = allowedBounds;
      const inBounds = latVal >= minLat && latVal <= maxLat && lngVal >= minLng && lngVal <= maxLng;
      if (!inBounds) {
        setGpsError(`Location must be inside the allowed area${allowedBoundsLabel ? ` (${allowedBoundsLabel})` : ''}.`);
        return;
      }
    }

    const payload = {
      ...form,
      unit: form.unit === '__other__' ? String(form.unitOther || '').trim() : form.unit,
    };
    if (editMode) {
      payload.quantity = form.quantity === '' ? null : Number(form.quantity);
      if (['released', 'left_in_place'].includes(form.harvestDetails?.resourceOutcome)) payload.quantity = 0;
      if (payload.quantity != null && form.harvestDetails?.caughtQuantity != null && form.harvestDetails.caughtQuantity !== ''
        && payload.quantity > Number(form.harvestDetails.caughtQuantity)) {
        setFormError('Quantity kept cannot exceed the caught / harvested quantity.');
        return;
      }
      payload.mediaUrls = String(form.mediaUrlsText || '').split('\n').map((url) => url.trim()).filter(Boolean);
      payload.harvestDetails = { ...form.harvestDetails };
      for (const key of ['caughtQuantity', 'sizeValue']) {
        const value = payload.harvestDetails[key];
        payload.harvestDetails[key] = value === '' || value == null ? null : Number(value);
      }
      delete payload.mediaUrlsText;
    }
    delete payload.unitOther;

    onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {(error || formError) && (
        <div role="alert" style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#991b1b', fontSize: 14,
        }}>
          {error || formError}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        <Field label="Category" required>
          <select value={form.category} onChange={set('category')} required style={inputStyle}>
            <option value="">Select category…</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </Field>
      </div>

      {isFieldEnabled('species') && (
        <Field label="Species / Resource name">
          {hasMapPickedLocation ? (
            <div style={{ ...inputStyle, background: '#f8fafc', color: '#1e293b' }}>
              {form.species || 'Not specified'}
            </div>
          ) : (
            <SpeciesAutocomplete
              value={form.species}
              onSelect={({ speciesId, speciesName, harvestCategory, speciesCategory }) => {
                const assignedCategory = resolveHarvestCategory({ speciesId, speciesName, harvestCategory, speciesCategory }, activeTaxonomy);
                setForm((prev) => {
                  const next = { ...prev, species: speciesName, speciesId: speciesId || '' };
                  if (assignedCategory && assignedCategory !== prev.category) {
                    next.category = assignedCategory;
                    next.subcategory = '';
                  }
                  return next;
                });
                if (onSpeciesSelect) onSpeciesSelect(speciesId, speciesName);
              }}
              popularSpecies={filteredPopular}
            />
          )}
          <SelectedSpeciesSummary speciesId={form.speciesId} />
        </Field>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        <Field label="Harvest date" required>
          <input
            type="date"
            value={form.harvestDate}
            onChange={set('harvestDate')}
            required
            style={inputStyle}
          />
        </Field>
      </div>

      {(isFieldEnabled('quantity') || isFieldEnabled('unit')) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {isFieldEnabled('quantity') && (
            <Field label="Quantity harvested">
              <input
                type="number"
                min={0}
                value={form.quantity}
                onChange={set('quantity')}
                placeholder="e.g. 5"
                style={inputStyle}
              />
            </Field>
          )}
          {editMode && <p>Quantity harvested is the amount kept. Released or left-in-place outcomes save zero kept;
            the caught / harvested quantity below records the total encountered.</p>}

          {isFieldEnabled('unit') && (
            <Field label="Unit of measurement">
              <select value={form.unit} onChange={set('unit')} style={inputStyle}>
                <option value="">Select unit…</option>
                {units.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
                <option value="__other__">Other</option>
              </select>
              {form.unit === '__other__' && (
                <input
                  type="text"
                  value={form.unitOther}
                  onChange={set('unitOther')}
                  placeholder="Enter custom unit"
                  style={{ ...inputStyle, marginTop: 8 }}
                />
              )}
            </Field>
          )}
        </div>
      )}

      {(isHuntingGame || editMode) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {isFieldEnabled('sex') && <Field label="Sex (optional)">
            <select
              value={form.harvestDetails?.sex || ''}
              onChange={setHarvestDetail('sex')}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>}
          {isFieldEnabled('size') && <Field label="Age or size (optional)">
            <input
              type="text"
              value={form.harvestDetails?.ageOrSizeClass || ''}
              onChange={setHarvestDetail('ageOrSizeClass')}
              placeholder="e.g. adult, juvenile, approximate size"
              style={inputStyle}
            />
          </Field>}
        </div>
      )}

      {isFishing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {!editMode && isFieldEnabled('size') && <Field label="Size (optional)">
            <input
              type="text"
              value={form.harvestDetails?.ageOrSizeClass || ''}
              onChange={setHarvestDetail('ageOrSizeClass')}
              placeholder="e.g. 45 cm"
              style={inputStyle}
            />
          </Field>}
          {isFieldEnabled('condition') && <Field label="Condition (optional)">
            <select
              value={form.harvestDetails?.condition || ''}
              onChange={setHarvestDetail('condition')}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="healthy">Healthy</option>
              <option value="injured">Injured</option>
              <option value="diseased_or_unusual">Diseased or unusual</option>
              <option value="dead_found">Dead when found</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>}
        </div>
      )}

      {isFieldEnabled('abundance') && (editMode || !usesFocusedHarvestQuestions || isGathering) && (
        <Field label={isGathering ? 'Abundance (optional)' : 'Observation abundance'}>
          <select value={form.abundance} onChange={set('abundance')} style={inputStyle}>
            <option value="">Not specified</option>
            {abundanceOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
            Use this for what you observed in the area, including when quantity harvested is 0.
          </p>
        </Field>
      )}

      <div style={{
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        borderRadius: 8, padding: '14px 16px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>
            📍 {hasMapPickedLocation ? 'Selected map location' : 'Location (optional)'}
          </div>
          {!hasMapPickedLocation && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={gpsLoading}
              style={{
                padding: '6px 14px', background: gpsLoading ? '#9ca3af' : '#0ea5e9', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: gpsLoading ? 'not-allowed' : 'pointer', lineHeight: 1.4,
              }}
            >
              {gpsLoading ? '⏳ Locating…' : '📍 Use My Location'}
            </button>
            {!basicMode && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                style={{
                  padding: '6px 14px', background: '#16a34a', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', lineHeight: 1.4,
                }}
              >
                🗺 Pick on Map
              </button>
            )}
          </div>}
        </div>
        {gpsError && (
          <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{gpsError}</div>
        )}

        {form.latitude !== '' && form.longitude !== '' && (
          <div style={{
            background: '#dcfce7', border: '1px solid #86efac',
            borderRadius: 6, padding: '7px 12px', marginBottom: 12,
            fontSize: 13, fontWeight: 600, color: '#166534',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>📍 {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}</span>
            {!hasMapPickedLocation && (
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, latitude: '', longitude: '' }))}
                style={{
                  background: 'none', border: 'none', color: '#166534',
                  cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '0 4px',
                }}
                title="Clear coordinates"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {!hasMapPickedLocation && (
          <>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Or enter manually:</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Latitude">
                <input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={set('latitude')}
                  placeholder="e.g. 51.0234"
                  style={inputStyle}
                />
              </Field>
              <Field label="Longitude">
                <input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={set('longitude')}
                  placeholder="e.g. -98.4567"
                  style={inputStyle}
                />
              </Field>
            </div>
          </>
        )}

        {isFieldEnabled('locationDescription') && (
          <Field label={hasMapPickedLocation ? 'Location notes (optional)' : 'Location description'}>
            <input
              type="text"
              value={form.locationDescription}
              onChange={set('locationDescription')}
              placeholder={hasMapPickedLocation ? 'Add any helpful notes about this location…' : 'e.g. North shore of Lake Winnipeg, near the creek mouth…'}
              style={inputStyle}
            />
          </Field>
        )}
      </div>

      {pickerOpen && (
        <CoordPickerModal
          harvestTenantId={tenantId}
          initLat={form.latitude}
          initLng={form.longitude}
          allowedBounds={allowedBounds}
          allowedBoundsLabel={allowedBoundsLabel}
          onConfirm={handlePickerConfirm}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {isFieldEnabled('observations') && (
        <Field label="Observations & notes">
          <textarea
            value={form.observations}
            onChange={set('observations')}
            rows={3}
            placeholder="Describe what you observed — conditions, wildlife, environmental notes…"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
      )}

      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setShowAdvanced((prev) => !prev)}
          aria-expanded={showAdvanced}
          style={{
            border: '1px solid #d1d5db',
            background: '#f8fafc',
            color: '#374151',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {showAdvanced ? 'Hide More Details' : 'More Details'}
        </button>
      </div>

      {showAdvanced && (
        <>
          {editMode && <>
            <Field label="Caught / harvested quantity">
              <input type="number" min="0" step="any" style={inputStyle} value={form.harvestDetails?.caughtQuantity ?? ''}
                onChange={setHarvestDetail('caughtQuantity')} />
            </Field>
            <Field label="Measured size">
              <input type="number" min="0" step="any" style={inputStyle} value={form.harvestDetails?.sizeValue ?? ''}
                onChange={setHarvestDetail('sizeValue')} />
            </Field>
            <Field label="Size unit">
              <select style={inputStyle} value={form.harvestDetails?.sizeUnit || ''} onChange={setHarvestDetail('sizeUnit')}>
                <option value="">Not specified</option>
                {['in', 'cm', 'lbs', 'kg'].map((unit) => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </Field>
            <Field label="Location accuracy">
              <select style={inputStyle} value={form.locationPrecision || ''} onChange={set('locationPrecision')}>
                <option value="">Not specified</option><option value="confirmed">Exact / confirmed</option>
                <option value="approximate">Approximate</option>
              </select>
            </Field>
            <Field label="Attachment URLs (one per line)">
              <textarea style={inputStyle} rows={3} value={form.mediaUrlsText || ''} onChange={set('mediaUrlsText')} />
            </Field>
          </>}
          {isFieldEnabled('subcategory') && (
            <Field label={subcatLabel}>
              <select
                value={form.subcategory}
                onChange={set('subcategory')}
                style={inputStyle}
                disabled={!subs.length && !editMode}
              >
                <option value="">None / Other</option>
                {subs.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {editMode && form.subcategory && !subs.includes(form.subcategory)
                  && <option value={form.subcategory}>{form.subcategory}</option>}
              </select>
            </Field>
          )}

          <Field label="Season override">
            <select value={form.season} onChange={set('season')} style={inputStyle}>
              <option value="">Select season…</option>
              {SEASONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {form.season && (
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                Automatically set from the harvest date unless changed here.
              </p>
            )}
          </Field>

          {isFieldEnabled('traditionalKnowledge') && (
            <Field label="Traditional knowledge & cultural notes">
              <textarea
                value={form.traditionalKnowledge}
                onChange={set('traditionalKnowledge')}
                rows={3}
                placeholder="Traditional uses, cultural significance, ancestral knowledge…"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </Field>
          )}

          {isFieldEnabled('isTraditionalUse') && (
            <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                id="isTraditionalUse"
                checked={form.isTraditionalUse}
                onChange={set('isTraditionalUse')}
                style={{ width: 16, height: 16 }}
              />
              <label htmlFor="isTraditionalUse" style={{ fontSize: 14, color: '#374151', cursor: 'pointer' }}>
                Mark as traditional use site / practice
              </label>
            </div>
          )}

          <div style={{ ...fieldStyle }}>
            <label style={labelStyle}>More details (optional)</label>
            <MoreDetailsSection
              resourceUseOptions={activeTaxonomy.resourceUseOptions}
              observedSignOptions={activeTaxonomy.observedSignOptions}
              isFieldEnabled={isFieldEnabled}
              values={form.harvestDetails || {}}
              category={form.category}
              allowUnset={editMode}
              onChange={(d) => setForm((p) => ({ ...p, harvestDetails: d }))}
            />
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '9px 22px',
            background: loading ? '#9ca3af' : '#16a34a',
            color: '#fff', border: 'none', borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Saving…' : editMode ? 'Save changes' : 'Submit Record'}
        </button>
        {onCancel && (
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            style={{
              padding: '9px 22px',
              background: '#f3f4f6', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
